import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MaleRoundedIcon from "@mui/icons-material/MaleRounded";
import FemaleRoundedIcon from "@mui/icons-material/FemaleRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { formatPhone, telHref, capitalize } from "../utils/npdCardHelpers";
import classes from "./style.module.scss";

// Same abbreviation style as before — 2-letter initials, computed
// generically from each real entity's actual name.

export function initialsFromName(name) {
  if (!name) return "—";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (name.slice(0, 2) || "—").toUpperCase();
}

export function truncateLabel(name, max = 16) {
  if (!name || name.length <= max) return name;
  return name.slice(0, max - 1).trimEnd() + "…";
}

const MAX_GRAPH_NODES = 10;

function mapsUrl(loc) {
  if (loc?.latitude != null && loc?.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`;
  }
  const q = [loc?.line, loc?.city, loc?.state, loc?.postalCode].filter(Boolean).join(" ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function formatAddress(loc) {
  // NPD shows ZIP+4 on mailing addresses only.
  const zip = loc.type === "mailing" && loc.postalCodeFull ? loc.postalCodeFull : loc.postalCode;
  const cityState = [loc.city, [loc.state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [loc.line, cityState].filter(Boolean).join(", ");
}

// NPD's date style: MM/DD/YY. Parsed from the string rather than via
// Date(), because CMS sometimes sends years like "0999" (NPD still
// renders those as ".../99", so we match it rather than showing "999").
function formatDate(raw) {
  const m = String(raw || "").match(/^(\d{1,4})-(\d{2})-(\d{2})/);
  if (!m) return raw || null;
  return `${m[2]}/${m[3]}/${m[1].padStart(4, "0").slice(-2)}`;
}

// "practice" + "fax" -> "Practice fax" (NPD's contact label format).
function contactLabel(c) {
  return c.use ? `${capitalize(c.use)} ${c.type}` : c.type;
}

function yesNo(value, yes = "Yes", no = "No", unknown = "Not provided") {
  if (value === true) return yes;
  if (value === false) return no;
  return unknown;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={classes["npd-copy-btn"]}
      aria-label={label}
      title={label}
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? "Copied" : <ContentCopyRoundedIcon sx={{ fontSize: 12 }} />}
    </button>
  );
}

// Small status dot for "At a glance" rows that have a real good/pending
// state — same tone classes (good/pending) the chip row right above
// this grid already uses for the same underlying fields, so a glance
// row and its matching chip never disagree on what color a status is.
function Dot({ tone }) {
  if (!tone) return null;
  return <span className={`${classes["npd-glance-dot"]} ${classes[tone]}`} />;
}

function Kv({ label, children }) {
  return (
    <div>
      <p className={classes["npd-meta-label"]}>{label}</p>
      <div className={classes["npd-meta-value"]}>{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <p className={classes["npd-empty"]}>{children}</p>;
}

function CardHead({ icon, title, sub, tint = "teal" }) {
  return (
    <div className={classes["npd-card-head"]}>
      <span
        className={classes["npd-card-icon"]}
        style={{ background: `var(--${tint}-tint)`, color: `var(--${tint}-ink)` }}
      >
        {icon}
      </span>
      <div>
        <p className={classes["npd-card-title"]}>{title}</p>
        {sub && <p className={classes["npd-card-sub"]}>{sub}</p>}
      </div>
    </div>
  );
}

// Collapsed-by-default section for secondary/dense detail (raw
// technical endpoint fields, record-provenance tables) that's real
// data worth keeping accessible, just not worth showing by default
// next to the information someone actually opened the card for.
function Disclosure({ label, openLabel, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={classes["npd-disclosure"]}>
      <button
        type="button"
        className={classes["npd-disclosure-toggle"]}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{open ? openLabel || label : label}</span>
        <KeyboardArrowDownRoundedIcon
          sx={{ fontSize: 16 }}
          className={classes["npd-disclosure-chevron"]}
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>
      {open && <div className={classes["npd-disclosure-body"]}>{children}</div>}
    </div>
  );
}

function ContactList({ contacts }) {
  if (!contacts?.length) return null;
  return (
    <div className={classes["npd-contact-list"]}>
      {contacts.map((c, i) => (
        <span key={`${c.type}-${c.value}-${i}`} className={classes["npd-contact"]}>
          <span className={classes["npd-contact-kind"]}>{contactLabel(c)}</span>
          {c.type === "phone" ? (
            <a href={telHref(c.value)}>{formatPhone(c.value)}{c.ext ? ` ext. ${c.ext}` : ""}</a>
          ) : c.type === "email" ? (
            <a href={`mailto:${c.value}`}>{c.value}</a>
          ) : (
            formatPhone(c.value)
          )}
        </span>
      ))}
    </div>
  );
}

function LocationRow({ loc }) {
  return (
    <div className={classes["npd-loc-block"]}>
      <a className={classes["npd-location-link"]} href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer">
        <span className={classes["npd-location-icon-badge"]}>
          <PlaceRoundedIcon sx={{ fontSize: 14 }} />
        </span>
        <span className={classes["npd-location-text"]}>{formatAddress(loc) || "Address not available"}</span>
        <NorthEastRoundedIcon sx={{ fontSize: 11 }} className={classes["npd-location-arrow"]} />
      </a>
      {(loc.county || loc.phone || loc.fax || loc.website || loc.wheelchairAccessible != null || loc.acceptingNewPatients != null || loc.officeHours) && (
        <p className={classes["npd-loc-meta"]}>
          {[
            loc.county,
            loc.phone && `Phone ${formatPhone(loc.phone)}`,
            loc.fax && `Fax ${formatPhone(loc.fax)}`,
            loc.wheelchairAccessible != null && `Wheelchair accessible: ${yesNo(loc.wheelchairAccessible)}`,
            loc.acceptingNewPatients != null && `Accepting new patients: ${yesNo(loc.acceptingNewPatients)}`,
            loc.officeHours && `Office hours: ${loc.officeHours}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          {loc.website && (
            <>
              {" · "}
              <a href={loc.website} target="_blank" rel="noopener noreferrer">Website</a>
            </>
          )}
        </p>
      )}
    </div>
  );
}

const LOCATIONS_PREVIEW_COUNT = 1;

// Affiliated organizations can list several practice locations each,
// and it's common for a provider affiliated with a large health
// system to have 4-6 addresses per org — capping the default view
// keeps "Practices and affiliations" scannable without dropping any
// real location, same pattern as the existing "Show N more
// organizations" toggle just below it. One location visible by
// default (not two), with a down-arrow chevron -- same visual
// language as the Disclosure component above -- instead of a "+"/"−"
// glyph, so "there's more here, tap to see it" reads at a glance.
function OrgLocations({ locations }) {
  const [showAll, setShowAll] = useState(false);
  if (!locations?.length) return <Empty>Address not available</Empty>;
  const visible = showAll ? locations : locations.slice(0, LOCATIONS_PREVIEW_COUNT);
  const hiddenCount = locations.length - LOCATIONS_PREVIEW_COUNT;
  return (
    <>
      {visible.map((loc, j) => <LocationRow loc={loc} key={j} />)}
      {locations.length > LOCATIONS_PREVIEW_COUNT && (
        <button
          type="button"
          className={classes["npd-toggle-locations"]}
          onClick={() => setShowAll((s) => !s)}
          aria-expanded={showAll}
        >
          {showAll ? "Show fewer locations" : `Show ${hiddenCount} more location${hiddenCount === 1 ? "" : "s"}`}
          <KeyboardArrowDownRoundedIcon
            sx={{ fontSize: 16 }}
            className={classes["npd-toggle-chevron"]}
            style={{ transform: showAll ? "rotate(180deg)" : "none" }}
          />
        </button>
      )}
    </>
  );
}

function EndpointCard({ ep }) {
  return (
    <div className={classes["npd-endpoint-card"]}>
      <div className={classes["npd-endpoint-head"]}>
        <span className={classes["npd-info-icon"]} style={{ position: "relative" }}>
          {ep.active && <span className={classes["npd-pulse"]} />}
          <WifiRoundedIcon sx={{ fontSize: 14 }} />
        </span>
        {/* Real endpointType ("FHIR"/"DIRECT", see ndhLookup.js) — a
            short, distinct value from ep.title's longer description,
            worth its own scannable badge rather than only appearing
            buried in the technical-details disclosure below. */}
        {ep.type && (
          <span className={`${classes["npd-endpoint-type-pill"]} ${classes["mono"]}`}>{ep.type}</span>
        )}
        <span className={classes["npd-endpoint-head-text"]}>
          <span className={classes["npd-endpoint-type"]}>{ep.title || ep.type || "Endpoint"}</span>
          {ep.owner && <span className={classes["npd-endpoint-owner"]}>{ep.owner}</span>}
        </span>
        <span className={`${classes["npd-status-chip"]} ${ep.active ? classes["good"] : classes["pending"]}`}>
          {ep.active === true ? "Active" : ep.active === false ? "Inactive" : "Unknown"}
        </span>
      </div>
      {ep.url && (
        <div className={classes["npd-endpoint-url"]}>
          <span className={classes["mono"]}>{ep.url}</span>
          <CopyButton text={ep.url} label="Copy endpoint" />
        </div>
      )}
    </div>
  );
}

/**
 * Real "Care team at this location" radial graph. Positions however
 * many real entities actually share this address evenly around the
 * center pin. Each node now shows a real, always-visible label —
 * "Dr. <initials>, <credential>" — not just hover-only initials, per
 * request. Credential comes from each team member's own real
 * Practitioner.qualification (see extractCredential in ndhLookup.js);
 * a person with no credential on file just shows "Dr. <initials>"
 * rather than a fabricated trailing comma. The full name is still
 * available on hover (native SVG <title>) as a bonus, not the primary
 * way to read who's who anymore.
 */
/**
 * Real version of the "care-team-card-redesign" Figma design — exact
 * colors/positions traced from its SVG source, not eyeballed from the
 * screenshot. Two real behavior changes from the previous version:
 * (1) hovering a node grows it, fills it solid teal, and turns its
 * connector line teal, matching the design's "active" node state; (2)
 * a dark tooltip card appears above the graph on hover, showing the
 * person's real name, credential, and specialty — a genuine FHIR field
 * (PractitionerRole.specialty) added specifically to power this, not
 * fabricated. "— Active Role" is honest here too: this list only ever
 * contains people with an active PractitionerRole in the first place
 * (see fetchCareTeamAtLocation), so the label is always true for every
 * node, not asserted independently of the real filter.
 */
function CareTeamGraph({ entities, onNodeClick }) {
  const width = 320;
  const height = 270;
  const cx = width / 2;
  const cy = 140;
  const satelliteRadius = 88;
  const nodeR = 15;
  const hoveredNodeR = 17;
  const centerR = 22;
  const n = entities.length;
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const positioned = entities.map((entity, i) => {
    const angleDeg = -90 + (360 / n) * i;
    const angle = (angleDeg * Math.PI) / 180;
    return {
      ...entity,
      x: cx + satelliteRadius * Math.cos(angle),
      y: cy + satelliteRadius * Math.sin(angle),
    };
  });

  const hovered = hoveredIndex != null ? positioned[hoveredIndex] : null;

  return (
    <div className={classes["care-team-graph-wrap"]}>
      {hovered && (
        <div className={classes["care-team-tooltip"]}>
          <p className={classes["care-team-tooltip-name"]}>
            {hovered.name}
            {hovered.credential ? `, ${hovered.credential}` : ""}
          </p>
          <p className={classes["care-team-tooltip-meta"]}>
            {hovered.specialty ? `${hovered.specialty} — ` : ""}Active Role
          </p>
        </div>
      )}
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        <circle
          cx={cx}
          cy={cy}
          r={satelliteRadius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth="1"
          strokeDasharray="4 4"
          opacity="0.6"
        />

        {positioned.map((node, i) => (
          <line
            key={`line-${i}`}
            x1={cx}
            y1={cy}
            x2={node.x}
            y2={node.y}
            stroke={i === hoveredIndex ? "#0D9488" : "#E2E8F0"}
            strokeWidth={i === hoveredIndex ? "2" : "1.5"}
            opacity={i === hoveredIndex ? "0.5" : "1"}
          />
        ))}

        <circle cx={cx} cy={cy} r={centerR} fill="#0D9488" />
        <g transform={`translate(${cx - 10}, ${cy - 11})`} stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M10 0C5.4 0 1.7 3.7 1.7 8.3c0 6.2 8.3 12.4 8.3 12.4s8.3-6.2 8.3-12.4C18.3 3.7 14.6 0 10 0z" />
          <circle cx="10" cy="8.3" r="2.6" />
        </g>

        {positioned.map((node, i) => {
          const isHovered = i === hoveredIndex;
          const r = isHovered ? hoveredNodeR : nodeR;
          // Last name only, not the full name — full name + credential
          // still shows on hover via the tooltip above. Showing the
          // full name permanently on every node is exactly what
          // collided at higher node counts in an earlier version of
          // this graph; last names are shorter and were verified
          // (via a real render test) to stay legible up to 10 nodes.
          //
          // "Lastname, MD" not "Dr. Lastname" — matches how NPD itself
          // (and every credential chip elsewhere in this file) presents
          // a clinician: name plus their real qualification, not an
          // assumed honorific. A person with no credential on file
          // just shows their last name, not a dangling ", ".
          const nameParts = (node.name || "").trim().split(/\s+/).filter(Boolean);
          const lastName = nameParts.length > 0 ? nameParts[nameParts.length - 1] : "";
          // node.credential is now the real FaCeT-credentialCS short
          // code (MD, DO, NP...), not its long display name -- see
          // extractCredential() in ndhLookup.js -- but truncate it too
          // just in case a future qualification ever comes through
          // with no short code and falls back to the long one, so a
          // single stray long credential can never blow out a node's
          // label the way "Doctor of Medicine" did before that fix.
          const label = lastName
            ? node.credential
              ? `${truncateLabel(lastName, 12)}, ${truncateLabel(node.credential, 6)}`
              : truncateLabel(lastName, 12)
            : "";
          const labelBelow = node.y >= cy;
          return (
            <g
              key={`node-${i}`}
              onClick={() => node.npi && onNodeClick?.(node.npi)}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ cursor: node.npi ? "pointer" : "default" }}
            >
              <circle
                cx={node.x}
                cy={node.y}
                r={r}
                fill={isHovered ? "#0F766E" : "white"}
                stroke={isHovered ? "#CCFBF1" : "#CBD5E1"}
                strokeWidth={isHovered ? "2" : "1.5"}
              />
              <text
                x={node.x}
                y={node.y + 3.5}
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                fill={isHovered ? "white" : "#475569"}
                style={{ pointerEvents: "none" }}
              >
                {initialsFromName(node.name)}
              </text>
              {label && (
                <text
                  x={node.x}
                  y={labelBelow ? node.y + r + 13 : node.y - r - 6}
                  textAnchor="middle"
                  fontSize="8.5"
                  fontWeight="600"
                  fill="#475569"
                  style={{ pointerEvents: "none" }}
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function primarySpecialtyForSummary(data) {
  const primary = data.specialties?.find((s) => s.isPrimary);
  if (primary) return `${primary.display} (Primary)`;
  return data.specialties?.[0]?.display || null;
}

/**
 * NpdRecordCards — the actual NPD-record card grid: Summary + At a
 * glance, Practices and affiliations, Care team, Endpoints, Insurance
 * & data network participation, Licenses & registrations.
 *
 * Pure presentational component — takes already-fetched `data` (the
 * shape parseNpdProvider() in ndhLookup.js produces, for either an
 * individual practitioner or an organization) rather than fetching it
 * itself. Shared by NpdDetailsTab (fetches by practitioner NPI) and
 * OrganizationDetailsModal (fetches by organization NPI via the
 * previously-unused fetchOrganizationFromNpd) so the two surfaces
 * render identically instead of drifting out of sync the way the
 * per-org endpoint listing had (it used to render inline here AND
 * again in the Endpoints card below — see the Endpoints section).
 *
 * `showCareTeam` hides the "Providers at this location" card — it's
 * always empty for an org fetched via fetchOrganizationFromNpd (that
 * path doesn't run the FHIR care-team query), so OrganizationDetailsModal
 * passes false rather than show a card that can never have content.
 *
 * `onOrgClick(org)`, when passed, makes each row in "Practices and
 * affiliations" open that organization's own full NPD record.
 *
 * Every empty state uses the same wording the NPD page uses, so a
 * missing value reads as "the directory doesn't have it", not a bug.
 */
export default function NpdRecordCards({ data, npi, fallbackName, onOrgClick, showCareTeam = true }) {
  const navigate = useNavigate();
  const [showAllOrgs, setShowAllOrgs] = useState(false);

  const displayName = data?.name || fallbackName || "Provider";
  const organizations = data.organizations || [];
  const primaryOrg = organizations[0];
  const endpoints = data.endpoints || [];
  const orgEndpoints = data.orgEndpoints || [];
  // Provider-level + org-level, combined — an org's endpoints used to
  // also render inline under "Practices and affiliations" (a genuine
  // duplicate of the same data); they now live only here.
  const allEndpoints = [...endpoints, ...orgEndpoints];
  // NPD summary line: primary specialty · the organization (only if there's
  // exactly one) · city/state (only if the unaffiliated practice addresses
  // resolve to exactly one).
  const uniq = (xs) => [...new Set(xs.filter(Boolean))];
  const summaryOrgs = uniq(organizations.map((o) => o.name));
  const summaryCities = uniq((data.practiceLocations || []).map((l) => (l.city && l.state ? `${l.city}, ${l.state}` : null)));
  const summaryLine = [
    primarySpecialtyForSummary(data),
    summaryOrgs.length === 1 ? summaryOrgs[0] : null,
    summaryCities.length === 1 ? summaryCities[0] : null,
  ].filter(Boolean).join(" · ");
  const accepting = data.acceptingPatients;
  const secondarySpecialties = (data.specialties || []).filter((s) => !s.isPrimary);
  const licensesHaveStatus = (data.licenses || []).some((l) => l.status);
  const licensesHavePortability = (data.licenses || []).some((l) => l.federalPortability?.length);
  const careTeam = data.careTeam || [];
  const graphTeam = careTeam.slice(0, MAX_GRAPH_NODES);
  const careLoc = data.careTeamLocation || primaryOrg?.locations?.[0] || data.practiceLocations?.[0] || null;
  const v = data.verification || {};
  const visibleOrgs = showAllOrgs ? organizations : organizations.slice(0, 3);
  const phonesFax = (data.contacts || []).filter((c) => c.type === "phone" || c.type === "fax" || c.type === "email");
  // The backend sometimes lists the same credential twice -- once as
  // {code: "MD", name: "Medical Doctor"} and again as a bare
  // {name: "MEDICAL DOCTOR"} entry -- rendering "MD Medical Doctor"
  // immediately followed by "MEDICAL DOCTOR". Dedupe by the
  // credential's own name (case/whitespace-insensitive), keeping
  // whichever copy appears first.
  const dedupedCredentials = (() => {
    const seen = new Set();
    const out = [];
    for (const c of data.credentials || []) {
      const key = (c.name || c.code || "").trim().toUpperCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  })();
  const primaryCredentialCode = dedupedCredentials[0]?.code;

  return (
    <div className={classes["npd-grid2"]}>
      {/* ---------------- Summary + At a glance ---------------- */}
      <div className={`${classes["npd-card"]} ${classes["npd-card-wide"]}`}>
        <div className={classes["npd-summary"]}>
          <div className={classes["npd-summary-head"]}>
            <span className={classes["npd-avatar"]}>{initialsFromName(displayName)}</span>
            <div className={classes["npd-summary-head-text"]}>
              <p className={classes["npd-eyebrow"]}>
                {data.npiType === "organization" ? "Organization provider" : "Individual provider"}
              </p>
              <p className={classes["npd-summary-name"]}>
                {displayName}
                {primaryCredentialCode && <span className={classes["npd-summary-credential"]}>, {primaryCredentialCode}</span>}
              </p>
              <p className={classes["npd-summary-npi"]}>
                NPI <span className={classes["mono"]}>{data.npi || npi}</span>
                <CopyButton text={data.npi || npi} label={`Copy NPI ${data.npi || npi}`} />
              </p>
              {summaryLine && <p className={classes["npd-summary-sub"]}>{summaryLine}</p>}
            </div>
          </div>
          <div className={classes["npd-tag-row"]}>
            {data.gender && (
              <span className={`${classes["npd-mini-chip"]} ${classes["neutral"]}`}>
                {data.gender.toLowerCase() === "male" ? (
                  <MaleRoundedIcon sx={{ fontSize: 12 }} />
                ) : data.gender.toLowerCase() === "female" ? (
                  <FemaleRoundedIcon sx={{ fontSize: 12 }} />
                ) : (
                  <PersonRoundedIcon sx={{ fontSize: 12 }} />
                )}
                {capitalize(data.gender)}
              </span>
            )}
            {accepting && (
              <span className={`${classes["npd-mini-chip"]} ${accepting.value ? classes["good"] : classes["neutral"]}`}>
                {accepting.value ? "Accepting patients" : "Not accepting patients"}
              </span>
            )}
            <span className={`${classes["npd-mini-chip"]} ${v.identityVerified ? classes["good"] : classes["pending"]}`}>
              {v.identityVerified ? "Identity verified" : `Identity verification ${String(v.identityStatus || "not started").toLowerCase()}`}
            </span>
            {v.attestationStatus != null && (
              <span className={`${classes["npd-mini-chip"]} ${v.attestationComplete ? classes["good"] : classes["pending"]}`}>
                {v.attestationComplete ? "NPD attestation complete" : "NPD attestation incomplete"}
              </span>
            )}
            {data.flags?.exclusionListed === true && (
              <span className={`${classes["npd-mini-chip"]} ${classes["bad"]}`}>On HHS exclusion list</span>
            )}
          </div>

          {/* Specialties lives with the name/credential/status chips now,
              not buried under the At-a-glance grid -- it's identity info,
              same as the rest of this header. The primary specialty is
              already shown up in the summary sub-line just above
              (primarySpecialtyForSummary), so repeating it in its own
              pill here was redundant -- this only lists whatever
              specialties aren't the primary one. */}
          <p className={classes["npd-meta-label"]} style={{ marginTop: 10 }}>Secondary specialty</p>
          {secondarySpecialties.length ? (
            <div className={classes["npd-tag-row"]}>
              {secondarySpecialties.map((s, i) => (
                <span
                  key={s.code || i}
                  className={`${classes["npd-specialty-pill"]} ${classes["secondary"]}`}
                  title={s.definition || undefined}
                >
                  {s.display}
                  {s.code ? ` · ${s.code}` : ""}
                </span>
              ))}
            </div>
          ) : (
            <Empty>Secondary specialty not available</Empty>
          )}
        </div>

        <div className={classes["npd-card-body"]}>
          {data.businessStructure && (
            <>
              <p className={classes["npd-meta-label"]} style={{ marginTop: 10 }}>Business structure</p>
              <p className={classes["npd-meta-value"]}>{data.businessStructure}</p>
            </>
          )}

          {data.authorizedOfficials?.length > 0 && (
            <>
              <p className={classes["npd-meta-label"]} style={{ marginTop: 10 }}>Authorized officials</p>
              <p className={classes["npd-meta-value"]}>
                {data.authorizedOfficials
                  .map((a) => [a.name, a.title, a.phone && formatPhone(a.phone)].filter(Boolean).join(" · "))
                  .join("; ")}
              </p>
            </>
          )}

          {data.otherNames?.length > 0 && (
            <>
              <p className={classes["npd-meta-label"]} style={{ marginTop: 10 }}>Other names</p>
              <p className={classes["npd-meta-value"]}>
                {data.otherNames.map((n) => `${n.name}${n.isFormer ? " (former)" : ""}`).join(", ")}
              </p>
            </>
          )}
          {data.affiliations?.length > 0 && (
            <>
              <p className={classes["npd-meta-label"]} style={{ marginTop: 10 }}>Program / business affiliations</p>
              <p className={classes["npd-meta-value"]}>
                {data.affiliations.map((a) => [a.name, a.relation && a.relation.replace(/_/g, " ")].filter(Boolean).join(" — ")).join("; ")}
              </p>
            </>
          )}
        </div>
      </div>

      {/* ---------------- Practices and affiliations ---------------- */}
      <div className={classes["npd-card"]}>
        <CardHead
          icon={<ApartmentRoundedIcon sx={{ fontSize: 15 }} />}
          title="Practices and affiliations"
          sub="Affiliated organizations, their practice locations and contacts."
        />
        <div className={classes["npd-card-body"]}>
          <p className={classes["npd-subhead"]}>Affiliated organizations</p>
          {organizations.length === 0 && <Empty>Organization not available</Empty>}
          {visibleOrgs.map((org, i) => (
            <div className={classes["npd-org-block"]} key={org.npi || i}>
              <div
                className={classes["npd-entity-box-row"]}
                style={{ padding: 0, cursor: org.npi && onOrgClick ? "pointer" : "default" }}
                onClick={org.npi && onOrgClick ? () => onOrgClick(org) : undefined}
                role={org.npi && onOrgClick ? "button" : undefined}
                tabIndex={org.npi && onOrgClick ? 0 : undefined}
                onKeyDown={
                  org.npi && onOrgClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOrgClick(org);
                        }
                      }
                    : undefined
                }
              >
                <span className={classes["npd-entity-box-name"]}>
                  {org.name}
                  {org.type && <span className={classes["npd-entity-box-type"]}> · Type: {org.type}</span>}
                  {org.managingOrgName && <span className={classes["npd-entity-box-type"]}> · managed by {org.managingOrgName}</span>}
                  {org.active === false && <span className={classes["npd-entity-box-type"]}> · inactive</span>}
                </span>
                {org.npi && onOrgClick && (
                  <span className={classes["npd-org-expand-hint"]}>
                    View organization
                    <NorthEastRoundedIcon sx={{ fontSize: 11 }} />
                  </span>
                )}
                {org.npi && (
                  <a
                    className={`${classes["npd-entity-box-npi"]} ${classes["mono"]}`}
                    href={`https://directory.cms.gov/organization/${org.npi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open organization NPI ${org.npi} in a new tab`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    NPI {org.npi}
                  </a>
                )}
              </div>
              {org.acceptingPatients === true && <p className={classes["npd-loc-meta"]}>Accepting new patients</p>}
              <p className={classes["npd-meta-label"]} style={{ marginTop: 8 }}>Locations</p>
              <OrgLocations locations={org.locations} />
              {org.contacts?.length > 0 && (
                <>
                  <p className={classes["npd-meta-label"]} style={{ marginTop: 8 }}>Contacts</p>
                  <ContactList contacts={org.contacts} />
                </>
              )}
            </div>
          ))}
          {organizations.length > 3 && (
            <button type="button" className={classes["npd-toggle-locations"]} onClick={() => setShowAllOrgs((s) => !s)}>
              {showAllOrgs ? "Show fewer organizations" : `Show ${organizations.length - 3} more organizations`}
              <span className={classes["npd-toggle-plus"]}>{showAllOrgs ? "−" : "+"}</span>
            </button>
          )}

          <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>Practice locations not associated with an organization</p>
          {data.practiceLocations?.length ? data.practiceLocations.map((loc, i) => <LocationRow loc={loc} key={i} />) : <Empty>Address not available</Empty>}

          <p className={classes["npd-subhead"]} style={{ marginTop: 14 }}>Mailing addresses not associated with an organization</p>
          {data.mailingAddresses?.length ? data.mailingAddresses.map((loc, i) => <LocationRow loc={loc} key={i} />) : <Empty>Mailing address not available</Empty>}

          <p className={classes["npd-subhead"]} style={{ marginTop: 14 }}>Phones and fax not associated with an organization</p>
          {phonesFax.length ? <ContactList contacts={phonesFax} /> : <Empty>Phone/fax not available.</Empty>}

          <p className={classes["npd-subhead"]} style={{ marginTop: 14 }}>Office hours</p>
          {data.officeHours ? <p className={classes["npd-meta-value"]}>{data.officeHours}</p> : <Empty>Office hours not available</Empty>}
        </div>
        <div className={classes["npd-card-foot"]}>
          <span className={classes["npd-source-note"]}>Sourced from the CMS National Provider Directory. Not Modus's own data.</span>
        </div>
      </div>

      {/* ---------------- Care team (FHIR PractitionerRole?location=) ---------------- */}
      {showCareTeam && (
        <div className={classes["npd-card"]}>
          <div className={classes["care-team-header"]}>
            <div className={classes["care-team-badge-row"]}>
              <span className={classes["care-team-icon-badge"]}>
                <GroupsRoundedIcon sx={{ fontSize: 14 }} />
              </span>
              <span className={classes["care-team-eyebrow"]}>Location network</span>
            </div>
            <p className={classes["care-team-title"]}>Providers at this location</p>
            {careLoc && <p className={classes["care-team-subtitle"]}>{[careLoc.line, careLoc.city, careLoc.state].filter(Boolean).join(", ")}</p>}
          </div>
          <div className={classes["npd-card-body"]}>
            {graphTeam.length > 0 && careLoc ? (
              <CareTeamGraph
                entities={graphTeam}
                onNodeClick={(memberNpi) => {
                  // Same restore-on-back mechanism as ProviderCard.jsx's "View all
                  // call logs" link (consumed by DetailCard.jsx) -- stash the
                  // doctor whose NPD tab is currently open before navigating away
                  // to a different care-team member's profile, so pressing Back on
                  // that page re-expands this doctor's row instead of losing it.
                  if (npi) sessionStorage.setItem("expandedDoctorNpi", npi);
                  navigate(`/base/doctor-profile/${memberNpi}`);
                }}
              />
            ) : (
              <p className={classes["npd-source-note"]}>No other providers currently have an active role listed at this address.</p>
            )}
          </div>
          <div className={classes["care-team-footer"]}>
            <span>
              {careTeam.length > MAX_GRAPH_NODES
                ? `Showing ${MAX_GRAPH_NODES} of ${careTeam.length} providers with an active role here`
                : careTeam.length > 0
                ? "Providers at this location"
                : "Queried directly by location, not inferred from this doctor's own affiliations."}
            </span>
            {careTeam.length > 0 && <NorthEastRoundedIcon sx={{ fontSize: 13, transform: "rotate(45deg)" }} />}
          </div>
        </div>
      )}

      {/* ---------------- Endpoints ---------------- */}
      <div className={classes["npd-card"]}>
        <CardHead
          icon={<WifiRoundedIcon sx={{ fontSize: 15 }} />}
          tint="green"
          title="Endpoints"
          sub="Used to request patient records, send a message to the provider, etc."
        />
        <div className={classes["npd-card-body"]}>
          {allEndpoints.length ? (
            allEndpoints.map((ep, i) => <EndpointCard ep={ep} key={`${ep.url}-${i}`} />)
          ) : (
            <Empty>No endpoints are listed for this provider or their affiliated organizations.</Empty>
          )}
        </div>
        <div className={classes["npd-card-foot"]}>
          <span className={classes["npd-source-note"]}>
            {allEndpoints.filter((e) => e.active).length} of {allEndpoints.length} endpoint{allEndpoints.length === 1 ? "" : "s"} active.
          </span>
        </div>
      </div>

      {/* ---------------- Insurance + data networks + telehealth ---------------- */}
      <div className={classes["npd-card"]}>
        <CardHead
          icon={<HubRoundedIcon sx={{ fontSize: 15 }} />}
          title="Insurance & network participation"
          sub="Coverage accepted, health-data networks, and telehealth."
        />
        <div className={classes["npd-card-body"]}>
          <p className={classes["npd-subhead"]}>Insurance participation</p>
          {data.insurance?.length ? (
            <div className={classes["npd-tag-row"]}>
              {data.insurance.map((p) => (
                <span className={classes["npd-tag"]} key={p}>{p}</span>
              ))}
            </div>
          ) : (
            <Empty>Insurance participation not available</Empty>
          )}

          <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>Data network participation</p>
          {data.dataNetworks?.length ? (
            <div className={classes["npd-tag-row"]}>
              {data.dataNetworks.map((n) => (
                <span className={classes["npd-tag"]} key={n}>{n}</span>
              ))}
            </div>
          ) : (
            <Empty>Data network participation data is not available yet.</Empty>
          )}

          <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>Telehealth</p>
          {data.telehealth?.length ? (
            data.telehealth.map((t, i) => (
              <dl className={classes["npd-dl"]} key={i}>
                {t.practiceName && (<div><dt>Practice name</dt><dd>{t.practiceName}</dd></div>)}
                {t.url && (<div><dt>Website</dt><dd><a href={t.url} target="_blank" rel="noopener noreferrer">{t.url}</a></dd></div>)}
                {t.states?.length > 0 && (<div><dt>States served</dt><dd>{t.states.join(", ")}</dd></div>)}
                {t.acceptingNewPatients != null && (<div><dt>Accepting new patients</dt><dd>{yesNo(t.acceptingNewPatients)}</dd></div>)}
              </dl>
            ))
          ) : (
            <Empty>No telehealth practice listed.</Empty>
          )}
        </div>
      </div>

      {/* ---------------- Licenses & registrations ---------------- */}
      <div className={`${classes["npd-card"]} ${classes["npd-card-wide"]}`}>
        <CardHead
          icon={<PersonRoundedIcon sx={{ fontSize: 15 }} />}
          title="Licenses & registrations"
          sub="State licenses, board certifications, DEA and CLIA, as published by NPD."
        />
        <div className={classes["npd-card-body"]}>
          <p className={classes["npd-subhead"]}>Licenses</p>
          {data.licenses?.length ? (
            <div className={classes["npd-table-wrap"]}>
              <table className={classes["npd-table"]} aria-label="State licenses">
                <thead>
                  <tr>
                    <th>License #</th><th>State</th><th>Issued</th><th>Expiration</th>
                    {licensesHaveStatus && <th>Status</th>}
                    {licensesHavePortability && <th>Federal license portability</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.licenses.map((l, i) => (
                    <tr key={`${l.state}-${l.number}-${i}`}>
                      <td className={classes["mono"]}>{l.number || "Not provided"}</td>
                      <td>{l.state || "Not provided"}</td>
                      <td>{formatDate(l.issued) || "Not provided"}</td>
                      <td>{formatDate(l.expires) || "Not provided"}</td>
                      {licensesHaveStatus && <td>{l.status || "Not provided"}</td>}
                      {licensesHavePortability && <td>{l.federalPortability?.join(", ") || "Not provided"}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty>License information is not available.</Empty>
          )}

          {data.boardCertifications?.length > 0 && (
            <>
              <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>Board certifications</p>
              {data.boardCertifications.map((b, i) => (
                <dl className={classes["npd-dl"]} key={i}>
                  {b.organization && (<div><dt>Issuing organization</dt><dd>{b.organization}</dd></div>)}
                  {b.name && (<div><dt>Certification</dt><dd>{b.name}</dd></div>)}
                  {b.number && (<div><dt>Certificate #</dt><dd>{b.number}</dd></div>)}
                  {b.expirationDate && (<div><dt>Expiration date</dt><dd>{formatDate(b.expirationDate) || b.expirationDate}</dd></div>)}
                </dl>
              ))}
            </>
          )}

          {data.dea?.length > 0 && (
            <>
              <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>DEA registration</p>
              {data.dea.map((d, i) => (
                <dl className={classes["npd-dl"]} key={i}>
                  {d.state && (<div><dt>State</dt><dd>{d.state}</dd></div>)}
                  {d.schedules?.length > 0 && (<div><dt>Schedules authorized</dt><dd>{d.schedules.join(", ")}</dd></div>)}
                  {d.expirationDate && (<div><dt>Expiration date</dt><dd>{formatDate(d.expirationDate) || d.expirationDate}</dd></div>)}
                </dl>
              ))}
              <p className={classes["npd-source-note"]}>DEA registration number is not published on the public directory.</p>
            </>
          )}

          {data.clia?.length > 0 && (
            <>
              <p className={classes["npd-subhead"]} style={{ marginTop: 16 }}>CLIA laboratory information</p>
              {data.clia.map((c, i) => (
                <dl className={classes["npd-dl"]} key={i}>
                  {c.certificateNumber && (<div><dt>Certificate #</dt><dd>{c.certificateNumber}</dd></div>)}
                  {c.certificateType && (<div><dt>Certificate type</dt><dd>{c.certificateType}</dd></div>)}
                  {c.effectiveDate && (<div><dt>Effective date</dt><dd>{formatDate(c.effectiveDate) || c.effectiveDate}</dd></div>)}
                  {c.isLaboratoryDirector && (<div><dt>Laboratory director</dt><dd>Yes</dd></div>)}
                </dl>
              ))}
            </>
          )}
        </div>
        <div className={classes["npd-card-body"]} style={{ paddingTop: 0 }}>
          <Disclosure label="Record history & data provenance" openLabel="Hide record history & data provenance">
            <div className={classes["npd-table-wrap"]}>
              <table className={classes["npd-table"]} aria-label="Record history">
                <thead>
                  <tr><th>Item</th><th>Value</th><th>Source</th></tr>
                </thead>
                <tbody>
                  {/* NPI enumerated dropped -- it's the same date
                      already shown up top as "Enumeration date", and
                      FHIR record last updated moved up next to the
                      Endpoints-available badge (see npd-tag-row
                      above), so it's not repeated here either. */}
                  <tr><td>Record last updated</td><td>{formatDate(data.recordHistory?.recordLastUpdated) || "Not available"}</td><td>{(data.recordHistory?.sourceSystems || []).join(" - ") || "Source not available"}</td></tr>
                </tbody>
              </table>
            </div>
          </Disclosure>
        </div>
        <div className={classes["npd-card-foot"]}>
          <span className={classes["npd-source-note"]}>
            Information shown is sourced from NPPES, PECOS, and provider-submitted updates via the CMS National Provider
            Directory{data.source === "fhir" ? " (FHIR fallback — some sections may be incomplete)" : ""}. Verify directly with the
            provider's office before scheduling.{" "}
            <a href={data.rawRecordUrl || `https://directory.cms.gov/provider/${npi}`} target="_blank" rel="noopener noreferrer">
              Open full NPD record
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}